/**
 * Module 22 — Phase A : Navigation contextuelle widgets cockpit + entités cliquables.
 *
 * Objectifs :
 *  - Rendre chaque widget du cockpit (élément avec [data-widget-id]) cliquable
 *    vers sa page de détail, avec feedback visuel hover.
 *  - Délégation globale : tout élément portant data-client-id / data-intervention-id /
 *    data-installation-id / data-partenaire-id / data-fournisseur-id devient cliquable
 *    vers sa fiche détail correspondante, sans avoir à le déclarer dans chaque module.
 *
 * Bootstrap : appeler `initWidgetsNavigation()` une seule fois au chargement de l'app.
 */

const WIDGET_NAVIGATION = {
  'widget-urgences-alertes':     '/dossiers-a-valider',
  'widget-kpi-cards':            '/finance',
  'widget-journal-caisse':       '/caisse',
  'widget-tresorerie-flash':     '/tresorerie',
  // 'widget-workboard' retiré (12/10/2026) : calendrier interactif, un clic dans le widget ne doit plus naviguer vers /planning
  'widget-ca-12mois':            '/finance',
  'widget-repartition-ca':       '/profitability',
  'widget-pipeline-dossiers':    '/installations',
  'widget-encours-fournisseurs': '/admin/fournisseurs',
  'widget-documents-envoi':      '/admin/documents-rapports',
  'widget-documents-rapports':   '/admin/documents-rapports',
  'widget-top-techniciens':      '/equipe/internes',
  'widget-top-vendeurs':         '/partenaires',
};

const ENTITY_URL_BUILDERS = {
  'client':        id => `/clients/${id}`,
  'intervention':  id => `/interventions/${id}`,
  'installation':  id => `/installations/${id}`,
  'fournisseur':   id => `/fournisseurs/${id}`,
  'partenaire':    id => `/partenaires`,
  'tech':          id => `/equipe/internes`,
  'document':      id => `/admin/documents-rapports`,
};

function getEntityUrl(type, id) {
  const fn = ENTITY_URL_BUILDERS[type];
  return fn ? fn(id) : null;
}

function isInteractive(target) {
  return !!target.closest('button, a, input, select, textarea, .dash-widget-actions, .dash-widget-refresh, [contenteditable="true"]');
}

let _installed = false;

export function initWidgetsNavigation() {
  if (_installed) return;
  _installed = true;

  injectStyles();
  attachGlobalClickHandler();

  const tryDecorate = () => decorateWidgets();
  tryDecorate();

  if (typeof MutationObserver !== 'undefined') {
    const obs = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.addedNodes.length) {
          for (const n of m.addedNodes) {
            if (n.nodeType !== 1) continue;
            if (n.hasAttribute?.('data-widget-id') ||
                n.querySelector?.('[data-widget-id]') ||
                n.classList?.contains('dash-slot') ||
                n.querySelector?.('.dash-slot')) {
              tryDecorate();
              break;
            }
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
}

function decorateWidgets() {
  document.querySelectorAll('[data-widget-id]').forEach(el => {
    const id = el.dataset.widgetId;
    const target = WIDGET_NAVIGATION[id];
    if (!target) return;
    if (el.dataset.navWired === '1') return;
    el.dataset.navWired = '1';
    el.classList.add('widget-clickable');
    el.style.cursor = 'pointer';
    el.setAttribute('role', 'link');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-label', `Ouvrir ${id.replace(/^widget-/, '').replace(/-/g, ' ')}`);
  });

  document.querySelectorAll('.dash-slot').forEach(slot => {
    if (slot.dataset.navWired === '1') return;
    const widget = slot.querySelector('[data-widget-id]');
    if (!widget) return;
    slot.dataset.navWired = '1';
    slot.style.cursor = 'pointer';
  });
}

function attachGlobalClickHandler() {
  document.addEventListener('click', e => {
    if (isInteractive(e.target)) return;

    const entityEl = e.target.closest('[data-entity-id][data-entity-type], [data-client-id], [data-intervention-id], [data-installation-id], [data-partenaire-id], [data-fournisseur-id]');
    if (entityEl) {
      let type = null, id = null;
      if (entityEl.dataset.entityId && entityEl.dataset.entityType) {
        type = entityEl.dataset.entityType;
        id = entityEl.dataset.entityId;
      } else if (entityEl.dataset.clientId) { type = 'client'; id = entityEl.dataset.clientId; }
      else if (entityEl.dataset.interventionId) { type = 'intervention'; id = entityEl.dataset.interventionId; }
      else if (entityEl.dataset.installationId) { type = 'installation'; id = entityEl.dataset.installationId; }
      else if (entityEl.dataset.partenaireId) { type = 'partenaire'; id = entityEl.dataset.partenaireId; }
      else if (entityEl.dataset.fournisseurId) { type = 'fournisseur'; id = entityEl.dataset.fournisseurId; }
      const url = type && id ? getEntityUrl(type, id) : null;
      if (url) {
        e.preventDefault();
        e.stopPropagation();
        window.location.hash = '#' + url;
        return;
      }
    }

    const widget = e.target.closest('[data-widget-id]');
    if (!widget) return;
    const wid = widget.dataset.widgetId;
    const url = WIDGET_NAVIGATION[wid];
    if (!url) return;
    e.preventDefault();
    window.location.hash = '#' + url;
  }, true);

  document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const widget = e.target.closest?.('[data-widget-id][role="link"]');
    if (!widget) return;
    const url = WIDGET_NAVIGATION[widget.dataset.widgetId];
    if (!url) return;
    e.preventDefault();
    window.location.hash = '#' + url;
  });
}

function injectStyles() {
  if (document.getElementById('widgets-navigation-styles')) return;
  const style = document.createElement('style');
  style.id = 'widgets-navigation-styles';
  style.textContent = `
    .widget-clickable {
      transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    }
    .widget-clickable:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba(15, 23, 42, 0.10);
    }
    .widget-clickable:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 3px;
    }
    [data-client-id], [data-intervention-id], [data-installation-id],
    [data-partenaire-id], [data-fournisseur-id],
    [data-entity-id][data-entity-type] {
      cursor: pointer;
      transition: color 0.12s ease, text-decoration-color 0.12s ease;
    }
    [data-client-id]:hover, [data-intervention-id]:hover, [data-installation-id]:hover,
    [data-partenaire-id]:hover, [data-fournisseur-id]:hover,
    [data-entity-id][data-entity-type]:hover {
      color: #2563eb;
      text-decoration: underline;
    }
    .client-link, .intervention-link, .installation-link, .partenaire-link, .fournisseur-link {
      cursor: pointer;
      color: #2563eb;
    }
    .client-link:hover, .intervention-link:hover, .installation-link:hover,
    .partenaire-link:hover, .fournisseur-link:hover { text-decoration: underline; }
  `;
  document.head.appendChild(style);
}

if (typeof window !== 'undefined') {
  window.initWidgetsNavigation = initWidgetsNavigation;
}

export default initWidgetsNavigation;
